import {
    Handler,
    SharedGestureState,
    WebKitGestureEvent,
    createUseGesture,
    dragAction,
    pinchAction
} from '@use-gesture/react';
import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { SeatType } from '@cineamo/api/models/seat/SeatDto.types';

import { Point2D } from '../helpers/math';
import { isTouchScreen } from '../helpers/screen-type';
import { tailwindColors } from '../helpers/tailwind-config-helper';
import { useResize } from '../hooks/useResize';
import { IconCircleSolid16 } from '../icons/ic-circleSolid';
import { IconClose16 } from '../icons/ic-close';
import { IconHeartSolid16 } from '../icons/ic-heartSolid';
import { IconWheelchair16 } from '../icons/ic-wheelchair';

import { Camera } from './Camera';
import { Drawer, DrawerOptions } from './Drawer';
import SeatTooltip from './SeatTooltip';
import { CinemaRoom, RoomObject, Seat } from './typedefs';

export type SeatMapData = {
    room: CinemaRoom;
    seats: Seat[];
    roomObjects?: RoomObject[];
};

export type SeatMapApi = {
    redraw(): void;
};

const useGesture = createUseGesture([dragAction, pinchAction]);

const DOUBLE_TAP_THRESHOLD = 300; // ms
const DOUBLE_TAP_DISTANCE = 30; // px

const iconComponents: Record<string, React.JSX.Element> = {
    dot: <IconCircleSolid16 fill={tailwindColors.darkBlue.DEFAULT} />,
    close: (
        <IconClose16
            fill={tailwindColors.white.DEFAULT}
            opacity={0.5}
        />
    ),
    heart_dark: (
        <IconHeartSolid16
            fill={tailwindColors.black.DEFAULT}
            opacity={0.5}
        />
    ),
    wheelchair_dark: (
        <IconWheelchair16
            fill={tailwindColors.black.DEFAULT}
            opacity={0.5}
        />
    ),
    heart_light: (
        <IconHeartSolid16
            fill={tailwindColors.white.DEFAULT}
            opacity={0.5}
        />
    ),
    wheelchair_light: (
        <IconWheelchair16
            fill={tailwindColors.white.DEFAULT}
            opacity={0.5}
        />
    )
};

function renderSvgToImage(icon: React.JSX.Element) {
    const markup = renderToStaticMarkup(icon);
    const encoded = encodeURIComponent(markup);
    const dataUri = `data:image/svg+xml;charset=utf-8,${encoded}`;
    const img = new Image();
    img.src = dataUri;
    img.crossOrigin = 'anonymous';
    return { img, dataUri };
}

type DefaultSeatMapProps = {
    data: SeatMapData;
    seatColorsByCategory: DrawerOptions['seatColorsByCategory'];
    className?: string;
};

type SeatMapProps = DefaultSeatMapProps &
    (
        | {
              isDisabled: true;
              onSeatClick?: never;
              onMouseEnter?: never;
              selectedSeatColor?: never;
              drawVersion?: never;
              trackEvent?: never;
          }
        | {
              isDisabled: false | undefined;
              onSeatClick: (seat: Seat) => void;
              onMouseEnter: () => void;
              selectedSeatColor: DrawerOptions['selectedSeatColor'];
              drawVersion: number;
              trackEvent: (event: string) => void;
          }
    );

const SeatMap: React.FC<SeatMapProps> = ({
    data,
    onSeatClick,
    onMouseEnter,
    seatColorsByCategory,
    selectedSeatColor,
    drawVersion,
    isDisabled,
    trackEvent,
    className
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasElRef = useRef<HTMLCanvasElement>(null);
    const drawerRef = useRef<Drawer>();
    const cameraRef = useRef<Camera>();
    const pixelRatio = useRef(1);
    const iconsRef = useRef<Record<string, HTMLImageElement>>({});
    const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null);
    const seatClickTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const dataRef = useRef(data);
    useEffect(() => {
        dataRef.current = data;
    });
    const t = useTranslations();


    const { width: containerWidth, height: containerHeight } = useResize(containerRef, 200);

    // Fill the container. On mobile (no container height), fall back to room aspect ratio. The room
    // can be missing while its query is still pending, so this must not assume one.
    const width = containerWidth;
    const height =
        containerHeight > 0 ? containerHeight : data.room ? (data.room.depth * containerWidth) / data.room.width : 0;

    const [cursor, setCursor] = useState('default');
    const [tooltip, setTooltip] = useState<{ message: string; position: { x: number; y: number } } | null>(null);

    const hasSelectedSeats = useMemo(() => {
        return data.seats?.some((seat) => seat.isSelected);
    }, [data.seats]);

    // Trigger redraw when selection state changes
    useEffect(() => {
        const drawer = drawerRef.current;
        const camera = cameraRef.current;
        if (drawer && camera) {
            drawer.drawFrame(dataRef.current, camera.state);
        }
    }, [hasSelectedSeats, data.seats?.length, data.room, drawVersion]);

    // Initialize icons and drawer
    useEffect(() => {
        pixelRatio.current = window.devicePixelRatio;

        const ctx = canvasElRef.current?.getContext('2d');
        async function prepareImages() {
            if (Object.keys(iconsRef.current).length > 0) {
                return;
            }
            await Promise.all(
                Object.entries(iconComponents).map(([key, icon]) => {
                    const { img } = renderSvgToImage(icon);
                    iconsRef.current[key] = img;
                    return new Promise((resolve) => {
                        img.onload = () => resolve([key, img]);
                    });
                })
            );
        }
        if (ctx) {
            prepareImages().then(() => {
                drawerRef.current = new Drawer(ctx, {
                    seatColorsByCategory,
                    selectedSeatColor,
                    screenLabel: t('tickets.screen'),
                    icons: iconsRef.current
                });
            });
        }
    }, [seatColorsByCategory, selectedSeatColor, t, pixelRatio]);

    // Initialize/update camera when dimensions change
    useEffect(() => {
        const drawer = drawerRef.current;
        // The room is guarded on its own rather than through `height`: the fallback above yields 0
        // without a room, but a container that has its own height keeps height non-zero, and the
        // room measurements below would then be read off nothing.
        if (!drawer || !width || !height || !data.room) return;

        const canvasW = width * pixelRatio.current;
        const canvasH = height * pixelRatio.current;

        // Compute actual room content height (seats + screen area)
        const roomScale = canvasW / data.room.width;
        const screenObject = data.roomObjects?.find((o) => o.type === 'screen');
        const screenDepth = screenObject ? screenObject.depth * roomScale : canvasH * 0.06 + 30;
        const contentH = data.room.depth * roomScale + screenDepth;

        if (cameraRef.current) {
            cameraRef.current.updateViewport(canvasW, canvasH, canvasW, contentH);
            drawer.drawFrame(data, cameraRef.current.state);
        } else {
            const camera = new Camera({
                viewportWidth: canvasW,
                viewportHeight: canvasH,
                contentWidth: canvasW,
                contentHeight: contentH,
                roomWidthMm: data.room.width,
                onUpdate: () => {
                    drawer.drawFrame(dataRef.current, camera.state);
                }
            });
            cameraRef.current = camera;
            // Initial draw
            drawer.drawFrame(data, camera.state);
        }
    }, [data, width, height]);

    // Cleanup
    useEffect(() => {
        return () => {
            cameraRef.current?.destroy();
            clearTimeout(seatClickTimeoutRef.current);
        };
    }, []);

    const onMouseMove = useCallback(
        ({ event: { offsetX, offsetY } }: SharedGestureState & { event: MouseEvent }) => {
            if (isTouchScreen) return;

            const drawer = drawerRef.current;
            const camera = cameraRef.current;
            if (!drawer || !camera) return;

            const x = offsetX * pixelRatio.current;
            const y = offsetY * pixelRatio.current;
            const worldPoint = camera.screenToWorld({ x, y });
            const hoveredSeat = drawer.findSeatByPoint(worldPoint);

            let newCursor = 'default';
            if (hoveredSeat) {
                const isNonSelectable =
                    hoveredSeat.isLocked || [SeatType.WHEELCHAIR, SeatType.DISABLED].includes(hoveredSeat.type);
                newCursor = isNonSelectable ? 'not-allowed' : 'pointer';

                // Show tooltip for non-selectable seats (only in interactive mode)
                if (isNonSelectable && !isDisabled) {
                    const isWheelchair = [SeatType.WHEELCHAIR, SeatType.DISABLED].includes(hoveredSeat.type);
                    const screenPos = camera.worldToScreen(worldPoint);
                    setTooltip({
                        message: isWheelchair ? t('tickets.seat-wheelchair-accessible') : t('tickets.seat-unavailable'),
                        position: {
                            x: screenPos.x / pixelRatio.current,
                            y: screenPos.y / pixelRatio.current
                        }
                    });
                } else {
                    setTooltip(null);
                }
            } else {
                setTooltip(null);
            }
            if (isDisabled) {
                newCursor = 'not-allowed';
            }
            setCursor(newCursor);
        },
        [isDisabled]
    );

    const handleSeatClick = useCallback(
        (screenX: number, screenY: number) => {
            if (isDisabled) return;

            const drawer = drawerRef.current;
            const camera = cameraRef.current;
            if (!drawer || !camera) return;

            const worldPoint = camera.screenToWorld({ x: screenX, y: screenY });
            const clickedSeat = drawer.findSeatByPoint(worldPoint);

            if (clickedSeat) {
                const isWheelchairOrDisabled = [SeatType.WHEELCHAIR, SeatType.DISABLED].includes(clickedSeat.type);

                if (isWheelchairOrDisabled || clickedSeat.isLocked) {
                    // Show tooltip for non-selectable seats on tap (touch screens)
                    const screenPos = camera.worldToScreen(worldPoint);
                    setTooltip({
                        message: isWheelchairOrDisabled
                            ? t('tickets.seat-wheelchair-accessible')
                            : t('tickets.seat-unavailable'),
                        position: {
                            x: screenPos.x / pixelRatio.current,
                            y: screenPos.y / pixelRatio.current
                        }
                    });
                    return;
                }

                if (!clickedSeat.isProcessing) {
                    clickedSeat.isSelected = !clickedSeat.isSelected;
                    drawer.drawFrame(dataRef.current, camera.state);
                    onSeatClick(clickedSeat);
                }
            } else {
                setTooltip(null);
            }
        },
        [onSeatClick, isDisabled]
    );

    const onClick = useCallback(
        ({ dragging, event: { offsetX, offsetY } }: SharedGestureState & { event: MouseEvent }) => {
            if (dragging) return;

            const x = offsetX * pixelRatio.current;
            const y = offsetY * pixelRatio.current;
            const now = Date.now();
            const lastTap = lastTapRef.current;

            // Double-tap detection
            if (
                lastTap &&
                now - lastTap.time < DOUBLE_TAP_THRESHOLD &&
                Math.hypot(x - lastTap.x, y - lastTap.y) < DOUBLE_TAP_DISTANCE * pixelRatio.current
            ) {
                // Double-tap detected
                lastTapRef.current = null;
                clearTimeout(seatClickTimeoutRef.current);
                cameraRef.current?.doubleTap({ x, y });
                trackEvent?.('Double Tap');
                return;
            }

            lastTapRef.current = { time: now, x, y };

            if (isTouchScreen) {
                // On touch: delay seat click to allow double-tap
                clearTimeout(seatClickTimeoutRef.current);
                seatClickTimeoutRef.current = setTimeout(() => {
                    handleSeatClick(x, y);
                }, DOUBLE_TAP_THRESHOLD);
            } else {
                // On desktop: immediate seat click
                handleSeatClick(x, y);
            }
        },
        [handleSeatClick, trackEvent]
    );

    const onDrag = useCallback<Handler<'drag', PointerEvent | MouseEvent | TouchEvent | KeyboardEvent>>(
        ({ pinching, cancel, first, last, delta: [x, y], velocity: [vx, vy], direction: [dx, dy] }) => {
            if (pinching) return cancel();

            const camera = cameraRef.current;
            if (!camera) return;

            if (first) {
                camera.beginGesture();
            }

            if (x !== 0 || y !== 0) {
                trackEvent?.('Drag');
            }

            const scaledDx = x * pixelRatio.current;
            const scaledDy = y * pixelRatio.current;
            camera.updatePan(scaledDx, scaledDy);

            if (!camera.isAnimating()) {
                // Manual redraw during direct gesture tracking
                const drawer = drawerRef.current;
                if (drawer) {
                    drawer.drawFrame(dataRef.current, camera.state);
                }
            }

            if (last) {
                const momentumScale = 800;
                camera.endPan(
                    vx * dx * momentumScale * pixelRatio.current,
                    vy * dy * momentumScale * pixelRatio.current
                );
            }
        },
        [trackEvent]
    );

    const onPinch = useCallback<Handler<'pinch', PointerEvent | TouchEvent | WheelEvent | WebKitGestureEvent>>(
        ({ canceled, ctrlKey, pinching, touches, first, last, delta: [ds], origin: [x, y] }) => {
            if (canceled || ctrlKey) return;
            // Allow `last` event through even when touches=0 (fingers lifted simultaneously)
            if (!last && (!pinching || !touches)) return;

            const camera = cameraRef.current;
            if (!camera) return;

            if (first) {
                camera.beginGesture();
                trackEvent?.('Pinch');
            }

            // Scale proportionally to pinch delta, boost at low zoom so first pinches feel responsive
            const currentScale = camera.state.scale;
            const sensitivity = 0.008 / Math.max(currentScale, 0.3);
            const factor = 1 + ds * sensitivity;
            const focal: Point2D = { x: x * pixelRatio.current, y: y * pixelRatio.current };
            camera.updateScale(factor, focal);

            if (!camera.isAnimating()) {
                const drawer = drawerRef.current;
                if (drawer) {
                    drawer.drawFrame(dataRef.current, camera.state);
                }
            }

            if (last) {
                camera.endScale();
            }
        },
        [trackEvent]
    );

    useGesture(
        {
            onMouseMove,
            onClick,
            onDrag,
            onPinch
        },
        {
            target: canvasElRef,
            drag: {
                filterTaps: true
            }
        }
    );

    // Custom wheel handler for smooth zoom
    useEffect(() => {
        const onWheel = (event: WheelEvent) => {
            const camera = cameraRef.current;
            if (!camera) return;

            trackEvent?.('Mouse Wheel');

            // Comparing with event.ctrlKey to support Mac-touchpad pinch-to-zoom
            const factor = event.deltaY > 0 !== event.ctrlKey ? 1.05 : 0.95;
            const focal: Point2D = {
                x: event.offsetX * pixelRatio.current,
                y: event.offsetY * pixelRatio.current
            };

            camera.zoomAtPoint(factor, focal);
            event.preventDefault();
        };

        const canvasEl = canvasElRef.current;
        if (canvasEl) {
            canvasEl.addEventListener('wheel', onWheel, { passive: false });
        }

        return () => {
            if (canvasEl) {
                canvasEl.removeEventListener('wheel', onWheel);
            }
        };
    }, [trackEvent]);

    return (
        <div
            ref={containerRef}
            className={classnames('relative', className)}>
            <canvas
                onMouseEnter={onMouseEnter}
                className={classnames('block touch-none select-none mx-auto', `cursor-${cursor}`)}
                style={{ width, height }}
                // eslint-disable-next-line react-hooks/refs -- intentional: device pixel ratio is captured once after mount and used to size the canvas backing store
                width={width * pixelRatio.current}
                // eslint-disable-next-line react-hooks/refs -- intentional: device pixel ratio is captured once after mount and used to size the canvas backing store
                height={height * pixelRatio.current}
                ref={canvasElRef}
            />
            <SeatTooltip
                message={tooltip?.message ?? null}
                position={tooltip?.position ?? { x: 0, y: 0 }}
            />
        </div>
    );
};

export default SeatMap;
